import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";
import { recordRecentWorkspace } from "../lib/recentWorkspaces";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// LocalStorage key for the role-preview shim. Persisted so an accidental
// page reload doesn't kick the admin back into their real permissions.
const PREVIEW_STORAGE_KEY = "iris_role_preview_v1";

function readStoredPreview() {
  try {
    const raw = localStorage.getItem(PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.permissions)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Effective permission slugs for the caller — populated on hydrate + login.
  // `null` while unknown; empty array = authenticated with no permissions.
  const [permissions, setPermissions] = useState(null);
  // Preview role — when set, the frontend gates every UI action as if the
  // caller had exactly this role's permission list. The backend still sees
  // the admin's real credentials, so this is a *visual* preview only.
  const [previewRole, setPreviewRole] = useState(() => readStoredPreview());

  const hydratePermissions = useCallback(async () => {
    try {
      const r = await api.get("/me/permissions");
      setPermissions(r.data?.permissions || []);
    } catch {
      setPermissions([]);
    }
  }, []);

  useEffect(() => {
    // Auth is cookie-based — call /auth/me once on mount. If the cookie is
    // valid the user is hydrated; otherwise we stay logged-out silently.
    (async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        await hydratePermissions();
      } catch {
        setUser(null);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [hydratePermissions]);

  // iter242 — wrapped in useCallback so the AuthContext `value` memo
  // keeps a stable identity across renders. Downstream consumers (Sidebar,
  // ProtectedRoute, etc.) subscribe via useContext and shouldn't re-render
  // just because the parent re-rendered.
  const login = useCallback(async (email, password, agencyId = null) => {
    const body = agencyId ? { email, password, agency_id: agencyId } : { email, password };
    const res = await api.post("/auth/login", body);
    if (res.data?.multi_agency) {
      return { multiAgency: true, ticket: res.data.ticket, agencies: res.data.agencies || [] };
    }
    setUser(res.data.user);
    await hydratePermissions();
    try { localStorage.removeItem(PREVIEW_STORAGE_KEY); } catch { /* ignore */ }
    setPreviewRole(null);
    recordRecentWorkspace({
      email: res.data.user?.email,
      agency_id: res.data.user?.agency_id,
      agency_name: res.data.user?.agency_name || null,
      role: res.data.user?.role,
    });
    return res.data.user;
  }, [hydratePermissions]);

  const loginSelectAgency = useCallback(async (ticket, agencyId, agencyName = null) => {
    const res = await api.post("/auth/login/select-agency", { ticket, agency_id: agencyId });
    setUser(res.data.user);
    await hydratePermissions();
    try { localStorage.removeItem(PREVIEW_STORAGE_KEY); } catch { /* ignore */ }
    setPreviewRole(null);
    recordRecentWorkspace({
      email: res.data.user?.email,
      agency_id: res.data.user?.agency_id,
      agency_name: agencyName || res.data.user?.agency_name || null,
      role: res.data.user?.role,
    });
    return res.data.user;
  }, [hydratePermissions]);

  const switchAgency = useCallback(async (agencyId, agencyName = null) => {
    const res = await api.post("/auth/switch-agency", { agency_id: agencyId });
    setUser(res.data.user);
    await hydratePermissions();
    try { localStorage.removeItem(PREVIEW_STORAGE_KEY); } catch { /* ignore */ }
    setPreviewRole(null);
    recordRecentWorkspace({
      email: res.data.user?.email,
      agency_id: res.data.user?.agency_id,
      agency_name: agencyName || res.data.user?.agency_name || null,
      role: res.data.user?.role,
    });
    return res.data.user;
  }, [hydratePermissions]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Logout call failed:", err);
    }
    setUser(null);
    setPermissions(null);
    try { localStorage.removeItem(PREVIEW_STORAGE_KEY); } catch { /* ignore */ }
    setPreviewRole(null);
  }, []);

  const startPreview = useCallback((role) => {
    if (!role || !Array.isArray(role.permissions)) return;
    const payload = {
      id: role.id,
      slug: role.slug,
      display_name: role.display_name,
      base_role: role.base_role,
      permissions: [...role.permissions],
    };
    try { localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    setPreviewRole(payload);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  const exitPreview = useCallback(() => {
    try { localStorage.removeItem(PREVIEW_STORAGE_KEY); } catch { /* ignore */ }
    setPreviewRole(null);
  }, []);

  const value = useMemo(
    () => ({
      user, setUser, loading, login, loginSelectAgency, switchAgency, logout,
      permissions,
      refreshPermissions: hydratePermissions,
      refreshUser,
      previewRole, startPreview, exitPreview,
      isPreviewing: !!previewRole,
      // Effective role / permissions — resolved through the preview shim
      // when active so downstream consumers (Sidebar, gated routes) can
      // treat preview mode consistently without knowing about it.
      effectiveRole: previewRole ? (previewRole.base_role || previewRole.slug) : user?.role,
      effectivePermissions: previewRole ? previewRole.permissions : permissions,
    }),
    [user, loading, permissions, hydratePermissions, refreshUser,
     login, loginSelectAgency, switchAgency, logout,
     previewRole, startPreview, exitPreview],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * usePermission("shifts:edit") → boolean
 *
 * Returns true if the current user has the requested permission. Returns
 * `false` while permissions are still loading (fail-closed — better to
 * momentarily hide a button than flash it and let the user click).
 *
 * When a role preview is active, the answer is derived *only* from the
 * previewed role's permission list — super_admin/admin shortcuts are
 * bypassed so previewing works even for the account-owner.
 */
export function usePermission(permission) {
  const { user, permissions, previewRole } = useAuth();
  if (!user) return false;
  // iter296 — same tardies↔attendance backwards-compat as the backend
  // fallback. Any role that pre-dates the tardies:* split still has
  // only attendance:*; treat those as implicitly granting the matching
  // tardies:*, so the Tardies tab doesn't vanish for existing users on
  // the day this ships.
  const matches = (list) => {
    if (!Array.isArray(list)) return false;
    if (list.includes(permission)) return true;
    if (permission && permission.startsWith("tardies:")) {
      const legacy = "attendance:" + permission.split(":", 2)[1];
      if (list.includes(legacy)) return true;
    }
    return false;
  };
  if (previewRole) return matches(previewRole.permissions);
  if (user.role === "super_admin") return true;
  return matches(permissions);
}
